import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { supabaseAdmin } from '@/integrations/supabase/client';

/**
 * Yearly Progress and Timeframe Booking Status (part 3e, 18 Sep 2026): how many weddings have every slot filled, for
 * the rest of this year and next year, and for the 30/60/90/120-day windows. Shown on the Coordination Overview and,
 * since Traci's doc (Oct 2026: "KPI additions - Talent"), on the Talent dashboard under its KPIs.
 * Data: GET /api/admin/coordination/stats (src/lib/coordination/stats.js staffingStats).
 */

const pctTone = (p: number | null) => (p === null ? 'text-muted-foreground' : p >= 85 ? 'text-emerald-700' : p >= 70 ? 'text-amber-700' : 'text-destructive');

function Bar({ pct }: { pct: number | null }) {
  return <div className="h-2 w-full rounded-full bg-muted"><div className={`h-2 rounded-full ${pct !== null && pct >= 85 ? 'bg-emerald-500' : pct !== null && pct >= 70 ? 'bg-amber-500' : 'bg-red-400'}`} style={{ width: `${pct ?? 0}%` }} /></div>;
}

export function StaffingProgressPanels({ data }: { data: any }) {
  const thisYear = String(data.today || new Date().toISOString()).slice(0, 4);
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="rounded-xl border bg-card p-4 shadow-sm" aria-label="Yearly progress">
        <h3 className="text-xs font-semibold uppercase text-muted-foreground">Yearly progress</h3>
        <div className="mt-3 space-y-3">
          {data.years.map((y: any) => (
            <div key={y.year}>
              <div className="flex items-center justify-between text-sm"><span className="font-medium">{y.year === thisYear ? `Remaining ${y.year} weddings` : `${y.year} weddings`} <span className="text-muted-foreground">({y.staffed}/{y.events})</span></span><span className={`tabular-nums ${pctTone(y.pct)}`}>{y.pct === null ? '—' : `${y.pct}%`}</span></div>
              <div className="mt-1"><Bar pct={y.pct} /></div>
            </div>
          ))}
        </div>
      </section>
      <section className="rounded-xl border bg-card p-4 shadow-sm" aria-label="Timeframe booking status">
        <h3 className="text-xs font-semibold uppercase text-muted-foreground">Timeframe booking status</h3>
        <table className="mt-2 w-full text-sm">
          <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-1 font-medium">Window</th><th className="py-1 text-right font-medium">Events</th><th className="py-1 text-right font-medium">Booked</th><th className="py-1 text-right font-medium">%</th></tr></thead>
          <tbody>
            {[...data.windows, { key: 'beyond', label: 'Beyond 120 days', ...data.beyond_120 }].map((w: any) => (
              <tr key={w.key} className="border-t">
                <td className="py-1.5 font-medium">{w.label}</td>
                <td className="py-1.5 text-right tabular-nums">{w.events}</td>
                <td className="py-1.5 text-right tabular-nums">{w.staffed}</td>
                <td className="py-1.5 text-right"><span className={`rounded px-1.5 py-0.5 text-xs font-semibold tabular-nums ${w.pct === null ? 'text-muted-foreground' : w.pct >= 85 ? 'bg-emerald-50 text-emerald-700' : w.pct >= 70 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}`}>{w.pct === null ? '—' : `${w.pct}%`}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

/** The two panels with their own data, for the Talent dashboard. */
export default function StaffingProgress() {
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { session } } = await supabaseAdmin.auth.getSession();
        if (!session) throw new Error('Not signed in');
        const res = await fetch('/api/admin/coordination/stats?horizon=120', { headers: { Authorization: `Bearer ${session.access_token}` } });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.message || body.error || `Request failed (${res.status})`);
        if (!cancelled) setData(body);
      } catch (e: any) { if (!cancelled) setError(e.message || String(e)); }
    })();
    return () => { cancelled = true; };
  }, []);
  if (error) return <p className="text-sm text-destructive">Could not load staffing progress: {error}</p>;
  if (!data) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Counting staffing…</div>;
  return <StaffingProgressPanels data={data} />;
}
